import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CreditCard, Banknote, MapPin, CheckCircle, XCircle, Loader, Tag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export function CheckoutPage() {
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const { cart, cartTotal, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [address, setAddress] = useState({
    street: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'Lebanon',
  });
  const [notes, setNotes] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [couponData, setCouponData] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [bulkRules, setBulkRules] = useState([]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
    } else if (cart.length === 0) {
      navigate('/cart');
    }
  }, [user, cart, navigate]);

  useEffect(() => {
    api.get('/api/bulk-discounts').then(res => setBulkRules(res.data)).catch(() => {});
  }, [api]);

  const shippingCost = cartTotal >= 50 ? 0 : 5;
  const couponDiscount = couponData?.discount_amount || 0;

  // Calculate bulk discount
  let bulkDiscount = 0;
  let activeBulkRule = null;
  for (let i = bulkRules.length - 1; i >= 0; i--) {
    if (cartTotal >= bulkRules[i].min_amount) {
      bulkDiscount = Math.round(cartTotal * (bulkRules[i].discount_percent / 100) * 100) / 100;
      activeBulkRule = bulkRules[i];
      break;
    }
  }

  const total = cartTotal + shippingCost - couponDiscount - bulkDiscount;

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    setCouponError('');
    setCouponData(null);
    try {
      const { data } = await api.post('/api/coupons/validate', {
        code: couponCode,
        subtotal: cartTotal,
      });
      setCouponData(data);
      toast.success(`Coupon applied: ${data.label}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Invalid coupon';
      setCouponError(msg);
      toast.error(msg);
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setCouponData(null);
    setCouponCode('');
    setCouponError('');
  };

  const handleAddressChange = (e) => {
    setAddress({ ...address, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!address.street || !address.city || !address.state || !address.postal_code) {
      toast.error('Please fill in all address fields');
      return;
    }

    setLoading(true);

    try {
      // Create order
      const { data: orderData } = await api.post('/api/orders', {
        shipping_address: address,
        payment_method: paymentMethod,
        notes,
        coupon_code: couponData?.code || null,
      });

      if (paymentMethod === 'card') {
        // Create Stripe checkout session
        const { data: checkoutData } = await api.post('/api/checkout/session', {
          order_id: orderData.id,
          origin_url: window.location.origin,
        });

        // Redirect to Stripe
        window.location.href = checkoutData.url;
      } else {
        // Cash on delivery - redirect to order detail
        toast.success('Order placed successfully!');
        // Cart is cleared on backend, just navigate
        navigate(`/dashboard/orders/${orderData.id}`);
      }
    } catch {
      toast.error('Failed to process order');
    } finally {
      setLoading(false);
    }
  };

  if (!user || cart.length === 0) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-heading font-bold text-navy mb-8">Checkout</h1>

        <form onSubmit={handleSubmit}>
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Checkout Form */}
            <div className="lg:col-span-2 space-y-6">
              {/* Shipping Address */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl p-6 border border-gray-200"
              >
                <h2 className="text-xl font-heading font-semibold text-navy mb-6 flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-mint" />
                  Shipping Address
                </h2>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <Label htmlFor="street">Street Address</Label>
                    <Input
                      id="street"
                      name="street"
                      value={address.street}
                      onChange={handleAddressChange}
                      placeholder="123 Main Street"
                      className="mt-2"
                      required
                      data-testid="checkout-street-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      name="city"
                      value={address.city}
                      onChange={handleAddressChange}
                      placeholder="Beirut"
                      className="mt-2"
                      required
                      data-testid="checkout-city-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="state">State/Region</Label>
                    <Input
                      id="state"
                      name="state"
                      value={address.state}
                      onChange={handleAddressChange}
                      placeholder="Mount Lebanon"
                      className="mt-2"
                      required
                      data-testid="checkout-state-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="postal_code">Postal Code</Label>
                    <Input
                      id="postal_code"
                      name="postal_code"
                      value={address.postal_code}
                      onChange={handleAddressChange}
                      placeholder="1100"
                      className="mt-2"
                      required
                      data-testid="checkout-postal-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="country">Country</Label>
                    <Input
                      id="country"
                      name="country"
                      value={address.country}
                      onChange={handleAddressChange}
                      placeholder="Lebanon"
                      className="mt-2"
                      disabled
                    />
                  </div>
                </div>
              </motion.div>

              {/* Payment Method */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white rounded-2xl p-6 border border-gray-200"
              >
                <h2 className="text-xl font-heading font-semibold text-navy mb-6 flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-mint" />
                  Payment Method
                </h2>

                <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
                  <Label
                    className={`flex items-center gap-4 p-4 border rounded-xl cursor-pointer transition-all ${
                      paymentMethod === 'card'
                        ? 'border-mint bg-mint/5'
                        : 'border-gray-200 hover:border-mint/50'
                    }`}
                  >
                    <RadioGroupItem value="card" />
                    <CreditCard className="h-5 w-5 text-navy" />
                    <div>
                      <span className="font-medium text-navy">Credit/Debit Card</span>
                      <p className="text-sm text-gray-500">Pay securely with Stripe</p>
                    </div>
                  </Label>

                  <Label
                    className={`flex items-center gap-4 p-4 border rounded-xl cursor-pointer transition-all mt-3 ${
                      paymentMethod === 'cash'
                        ? 'border-mint bg-mint/5'
                        : 'border-gray-200 hover:border-mint/50'
                    }`}
                  >
                    <RadioGroupItem value="cash" />
                    <Banknote className="h-5 w-5 text-navy" />
                    <div>
                      <span className="font-medium text-navy">Cash on Delivery</span>
                      <p className="text-sm text-gray-500">Pay when you receive your order</p>
                    </div>
                  </Label>
                </RadioGroup>
              </motion.div>

              {/* Order Notes */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-2xl p-6 border border-gray-200"
              >
                <h2 className="text-xl font-heading font-semibold text-navy mb-4">
                  Order Notes (Optional)
                </h2>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any special instructions for your order..."
                  rows={3}
                  data-testid="checkout-notes-input"
                />
              </motion.div>

              {/* Coupon Code */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white rounded-2xl p-6 border border-gray-200"
              >
                <h2 className="text-xl font-heading font-semibold text-navy mb-4 flex items-center gap-2">
                  <Tag className="h-5 w-5 text-mint" />
                  Coupon Code
                </h2>
                {couponData ? (
                  <div className="flex items-center justify-between bg-mint/5 border border-mint/20 rounded-xl p-3">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-mint/20 text-navy">{couponData.code}</Badge>
                      <span className="text-sm font-medium text-navy">{couponData.label}</span>
                      <span className="text-sm text-gray-500">(-${couponData.discount_amount.toFixed(2)})</span>
                    </div>
                    <Button variant="ghost" size="sm" onClick={removeCoupon} className="text-red-500 text-xs" data-testid="remove-coupon-btn">
                      Remove
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      value={couponCode}
                      onChange={(e) => { setCouponCode(e.target.value); setCouponError(''); }}
                      placeholder="Enter coupon code"
                      className="flex-1"
                      data-testid="coupon-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleApplyCoupon}
                      disabled={couponLoading || !couponCode.trim()}
                      data-testid="apply-coupon-btn"
                    >
                      {couponLoading ? 'Checking...' : 'Apply'}
                    </Button>
                  </div>
                )}
                {couponError && <p className="text-red-500 text-sm mt-2" data-testid="coupon-error">{couponError}</p>}
              </motion.div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl p-6 border border-gray-200 sticky top-24"
              >
                <h2 className="text-xl font-heading font-semibold text-navy mb-6">Order Summary</h2>

                <div className="space-y-3 mb-6">
                  {cart.map((item) => (
                    <div key={item.id} className="flex justify-between text-sm">
                      <span className="text-gray-600">
                        {item.product?.name} x{item.quantity}
                      </span>
                      <span className="font-medium">
                        ${(item.unit_price * item.quantity * (item.options?.quantity_tier || 1)).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="border-t border-gray-200 pt-4 space-y-3">
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotal</span>
                    <span>${cartTotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Shipping</span>
                    <span>{shippingCost === 0 ? 'Free' : `$${shippingCost.toFixed(2)}`}</span>
                  </div>
                  {couponDiscount > 0 && (
                    <div className="flex justify-between text-mint font-medium">
                      <span>Coupon ({couponData?.code})</span>
                      <span>-${couponDiscount.toFixed(2)}</span>
                    </div>
                  )}
                  {bulkDiscount > 0 && (
                    <div className="flex justify-between text-mint font-medium" data-testid="bulk-discount-line">
                      <span>Bulk Discount ({activeBulkRule?.discount_percent}%)</span>
                      <span>-${bulkDiscount.toFixed(2)}</span>
                    </div>
                  )}
                  {/* Bulk discount tiers info */}
                  {bulkRules.length > 0 && !activeBulkRule && (
                    <div className="bg-mint/5 rounded-xl p-3 border border-mint/10">
                      <p className="text-xs font-medium text-navy mb-1.5">Bulk Order Savings</p>
                      {bulkRules.map(rule => (
                        <p key={rule.id || rule.min_amount} className="text-xs text-gray-500">
                          {rule.label}
                        </p>
                      ))}
                    </div>
                  )}
                  <div className="border-t border-gray-200 pt-3 flex justify-between">
                    <span className="font-semibold text-navy">Total</span>
                    <span className="font-bold text-xl text-navy">${total.toFixed(2)}</span>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full mt-6 bg-mint hover:bg-mint-dark text-black py-6 rounded-full btn-hover"
                  data-testid="place-order-btn"
                >
                  {loading ? (
                    <>
                      <Loader className="mr-2 h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    paymentMethod === 'card' ? 'Pay with Card' : 'Place Order'
                  )}
                </Button>
              </motion.div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const { clearCart } = useCart();
  const [status, setStatus] = useState('checking');
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const pollPaymentStatus = async (attempts = 0) => {
      if (attempts >= 5) {
        setStatus('timeout');
        return;
      }

      try {
        const { data } = await api.get(`/api/checkout/status/${sessionId}`);
        
        if (data.payment_status === 'paid') {
          setStatus('success');
          await clearCart();
        } else if (data.status === 'expired') {
          setStatus('failed');
        } else {
          setTimeout(() => pollPaymentStatus(attempts + 1), 2000);
        }
      } catch {
        setStatus('error');
      }
    };

    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-3xl p-8 max-w-md w-full text-center shadow-lg border border-gray-200"
      >
        {status === 'checking' && (
          <>
            <Loader className="h-16 w-16 mx-auto text-mint animate-spin mb-6" />
            <h1 className="text-2xl font-heading font-bold text-navy">Processing Payment...</h1>
            <p className="text-gray-500 mt-2">Please wait while we confirm your payment</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="h-16 w-16 mx-auto text-mint mb-6" />
            <h1 className="text-2xl font-heading font-bold text-navy">Payment Successful!</h1>
            <p className="text-gray-500 mt-2">{"Thank you for your order. We'll send you a confirmation email shortly."}</p>
            <Button
              onClick={() => navigate('/dashboard/orders')}
              className="mt-6 bg-mint hover:bg-mint-dark text-black rounded-full"
              data-testid="view-orders-btn"
            >
              View Orders
            </Button>
          </>
        )}

        {(status === 'failed' || status === 'error' || status === 'timeout') && (
          <>
            <XCircle className="h-16 w-16 mx-auto text-coral mb-6" />
            <h1 className="text-2xl font-heading font-bold text-navy">Payment Failed</h1>
            <p className="text-gray-500 mt-2">
              {status === 'timeout'
                ? 'Payment verification timed out. Please check your orders or contact support.'
                : 'Something went wrong with your payment. Please try again.'}
            </p>
            <Button
              onClick={() => navigate('/cart')}
              className="mt-6 bg-coral hover:bg-coral-dark text-white rounded-full"
            >
              Return to Cart
            </Button>
          </>
        )}
      </motion.div>
    </div>
  );
}

export function CheckoutCancelPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-3xl p-8 max-w-md w-full text-center shadow-lg border border-gray-200"
      >
        <XCircle className="h-16 w-16 mx-auto text-gray-400 mb-6" />
        <h1 className="text-2xl font-heading font-bold text-navy">Payment Cancelled</h1>
        <p className="text-gray-500 mt-2">Your order has not been processed. Your cart items are still saved.</p>
        <div className="flex gap-4 justify-center mt-6">
          <Button
            onClick={() => navigate('/cart')}
            variant="outline"
            className="rounded-full"
          >
            Return to Cart
          </Button>
          <Button
            onClick={() => navigate('/products')}
            className="bg-mint hover:bg-mint-dark text-black rounded-full"
          >
            Continue Shopping
          </Button>
        </div>
      </motion.div>
    </div>
  );
}
